/**
 * Wrapper component for Navbar that provides consistent authentication state
 * Handles authentication logic internally and shows appropriate navbar
 */

import { Navbar } from '@finansafe/design-system';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';

interface NavbarWrapperProps {
  className?: string;
}

export function NavbarWrapper({ className }: NavbarWrapperProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, hasFullAccess, userName, logout, isLoading, isEnterprise } = useAuth();

  const publicRoutes = ['/login', '/register'];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  // For navbar display: show authenticated features for any authenticated user
  // Navigation protection will be handled by individual route guards
  const finalIsAuthenticated = isPublicRoute ? false : isAuthenticated;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <img
                src="/finansafe.png"
                alt="FinanSafe Logo"
                className="w-8 h-8 object-contain brightness-0"
              />
              <span className="text-xl font-bold text-gray-900">FinanSafe</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Always show the navbar, but adjust authentication state based on current route
  return (
    <Navbar
      isAuthenticated={finalIsAuthenticated}
      userName={userName}
      onLogout={handleLogout}
      className={className}
      isEnterprise={isEnterprise}
    />
  );
}
